import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
interface ThreeBackgroundProps {
  className?: string;
}
export function ThreeBackground({
  className
}: ThreeBackgroundProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!mountRef.current) return;
    // Setup Scene, Camera, Renderer
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 15;
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mountRef.current.appendChild(renderer.domElement);
    // Geometries
    const geometries = [new THREE.IcosahedronGeometry(1, 0), new THREE.TorusGeometry(1, 0.4, 8, 16), new THREE.OctahedronGeometry(1, 0)];
    // Materials
    const goldMaterial = new THREE.MeshBasicMaterial({
      color: 0xd4a853,
      wireframe: true,
      transparent: true,
      opacity: 0.15
    });
    const navyMaterial = new THREE.MeshBasicMaterial({
      color: 0x4f83cc,
      wireframe: true,
      transparent: true,
      opacity: 0.1
    });
    const materials = [goldMaterial, navyMaterial];
    // Create Meshes
    const meshes: {
      mesh: THREE.Mesh;
      baseX: number;
      baseY: number;
      speed: number;
      phase: number;
      rotationSpeed: THREE.Vector3;
    }[] = [];
    const numObjects = 20;
    for (let i = 0; i < numObjects; i++) {
      const geometry = geometries[Math.floor(Math.random() * geometries.length)];
      const material = materials[Math.floor(Math.random() * materials.length)];
      const mesh = new THREE.Mesh(geometry, material);
      // Random position spread across the screen
      const baseX = (Math.random() - 0.5) * 40;
      const baseY = (Math.random() - 0.5) * 40;
      const z = (Math.random() - 0.5) * 20 - 5; // Depth between -15 and 5
      mesh.position.set(baseX, baseY, z);
      // Random scale for depth-of-field feel
      const scale = Math.random() * 1.5 + 0.5;
      mesh.scale.set(scale, scale, scale);
      // Random initial rotation
      mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
      scene.add(mesh);
      meshes.push({
        mesh,
        baseX,
        baseY,
        speed: Math.random() * 0.002 + 0.001,
        phase: Math.random() * Math.PI * 2,
        rotationSpeed: new THREE.Vector3((Math.random() - 0.5) * 0.005, (Math.random() - 0.5) * 0.005, (Math.random() - 0.5) * 0.005)
      });
    }
    // Animation Loop
    let animationFrameId: number;
    let time = 0;
    const animate = () => {
      time += 1;
      meshes.forEach(data => {
        // Slow rotation
        data.mesh.rotation.x += data.rotationSpeed.x;
        data.mesh.rotation.y += data.rotationSpeed.y;
        data.mesh.rotation.z += data.rotationSpeed.z;
        // Gentle floating motion using sine waves
        data.mesh.position.y = data.baseY + Math.sin(time * data.speed + data.phase) * 2;
        data.mesh.position.x = data.baseX + Math.cos(time * data.speed * 0.8 + data.phase) * 1;
      });
      renderer.render(scene, camera);
      animationFrameId = requestAnimationFrame(animate);
    };
    animate();
    // Resize Handler
    const handleResize = () => {
      if (!mountRef.current) return;
      const width = window.innerWidth;
      const height = window.innerHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      geometries.forEach(g => g.dispose());
      materials.forEach(m => m.dispose());
      renderer.dispose();
    };
  }, []);
  return <div ref={mountRef} className={className} data-id="element-243" />;
}